import { useRef } from 'react';

import { cn } from 'support/helpers/cn/cn';

export type TabBarItem<TId extends string> = {
  id: TId;
  label: string;
};

type TabBarProps<TId extends string> = {
  tabs: Array<TabBarItem<TId>>;
  activeTab: TId;
  onClickTab: (id: TId) => void;
};

export const TabBar = <TId extends string>({ tabs, activeTab, onClickTab }: TabBarProps<TId>) => {
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  if (tabs.length <= 1) {
    return null;
  }

  const handleKeyDown = (evt: React.KeyboardEvent, index: number) => {
    let nextIndex: number | null = null;
    if (evt.key === 'ArrowRight') {
      nextIndex = (index + 1) % tabs.length;
    }
    if (evt.key === 'ArrowLeft') {
      nextIndex = (index - 1 + tabs.length) % tabs.length;
    }
    if (nextIndex !== null) {
      evt.preventDefault();
      buttonRefs.current[nextIndex]?.focus();
      onClickTab(tabs[nextIndex].id);
    }
  };

  // t('webedi:sidebar.tabs.overview')
  // t('webedi:sidebar.tabs.agents')
  // t('webedi:sidebar.tabs.viewPDF')

  return (
    <div role="tablist" className="sticky top-0 z-10 flex border-b border-gray-200 bg-white p-0!">
      {tabs.map((tab, index) => (
        <button
          key={tab.id}
          ref={(el) => {
            buttonRefs.current[index] = el;
          }}
          type="button"
          role="tab"
          id={`tab-${tab.id}`}
          aria-selected={tab.id === activeTab}
          aria-controls={`panel-${tab.id}`}
          className={cn(
            'cursor-pointer border-b-2 border-transparent p-4 pb-[14px] text-sm leading-5 font-medium text-gray-500',
            tab.id === activeTab && 'border-b-gray-700 text-gray-700',
          )}
          onClick={(evt) => {
            evt.preventDefault();
            onClickTab(tab.id);
          }}
          onKeyDown={(evt) => handleKeyDown(evt, index)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
};
